'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Paper, Title, Text, SimpleGrid, Box, Group, Anchor } from '@mantine/core';
import { IconChecklist, IconLifebuoy, IconMessageReport, IconArrowRight } from '@tabler/icons-react';
import api from '@/lib/api';
import { getThumbUrl } from '@/lib/imageUrl';
import { trackEvent } from '@/lib/telemetry';

const LEGO_REPLACEMENT_PARTS_URL = 'https://www.lego.com/service/replacement-parts';
const RELATED_LIMIT = 4;

// Einde van de setpagina: in plaats van doodlopen na de laatste bag een paar
// logische vervolgstappen + meer sets uit hetzelfde thema. De thema-sets worden
// pas opgehaald als het blok bijna in beeld is (de meeste bezoekers scrollen
// hier nooit naartoe).
export function WhatsNext({ set, sortProgress }) {
  const rootRef = useRef(null);
  const [related, setRelated] = useState(null);

  useEffect(() => {
    if (!set?.theme || !rootRef.current) return;
    let cancelled = false;
    const controller = new AbortController();
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      api.fetchSets(set.theme, null, RELATED_LIMIT + 1, 0, 'popular', controller.signal)
        .then(data => {
          if (cancelled) return;
          const others = (data?.sets || []).filter(s => String(s.id) !== String(set.id));
          setRelated(others.slice(0, RELATED_LIMIT));
        })
        .catch(() => { if (!cancelled) setRelated([]); });
    }, { rootMargin: '400px' });
    observer.observe(rootRef.current);
    return () => {
      cancelled = true;
      observer.disconnect();
      controller.abort();
    };
  }, [set?.id, set?.theme]);

  if (!set) return null;

  const track = (target) => trackEvent('Whats Next Click', { target, set: String(set.id) });
  const sortStarted = (sortProgress?.checked || 0) > 0;
  const sortDone = (sortProgress?.percentage || 0) >= 100;

  const actions = [
    {
      key: 'sort',
      icon: IconChecklist,
      color: '#1971c2',
      bg: '#e7f5ff',
      title: sortDone ? 'Your set is complete' : 'Check for missing parts',
      text: sortDone
        ? 'Every part is ticked off. Open Sort mode to review or start over.'
        : 'Tick off every part bag by bag. Sort mode lists whatever is missing.',
      cta: sortDone ? 'Open Sort mode' : sortStarted ? 'Continue sorting' : 'Start sorting',
      href: `/set/${set.id}/sort`,
    },
    {
      key: 'replacement',
      icon: IconLifebuoy,
      color: '#e8590c',
      bg: '#fff4e6',
      title: 'Missing a part?',
      text: 'LEGO sends replacement parts for sets you own, usually free of charge.',
      cta: 'Request from LEGO',
      href: LEGO_REPLACEMENT_PARTS_URL,
      external: true,
    },
    {
      key: 'report',
      icon: IconMessageReport,
      color: '#495057',
      bg: '#f1f3f5',
      title: 'Spotted a mistake?',
      text: "A part in the wrong bag or a missing image? Let us know and we'll fix it.",
      cta: 'Report an issue',
      href: `/report?set=${set.id}`,
    },
  ];

  return (
    <Paper ref={rootRef} component="section" aria-labelledby="whats-next-title" radius="lg" p={{ base: 'md', sm: 'xl' }} mt="xl" className="whats-next">
      <Title order={2} id="whats-next-title" size="h3" mb="md" style={{ fontWeight: 700, color: '#212529' }}>
        What&apos;s next?
      </Title>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
        {actions.map(({ key, icon: Icon, color, bg, title, text, cta, href, external }) => {
          const linkProps = external
            ? { component: 'a', href, target: '_blank', rel: 'noopener noreferrer' }
            : { component: Link, href };
          return (
            <Box key={key} {...linkProps} className="whats-next-action" onClick={() => track(key)}>
              <Box className="whats-next-icon" style={{ backgroundColor: bg }}>
                <Icon size={22} color={color} stroke={1.8} aria-hidden="true" />
              </Box>
              <Box style={{ minWidth: 0 }}>
                <Text fw={700} size="sm" style={{ color: '#212529' }}>{title}</Text>
                <Text size="xs" c="dimmed" mt={2}>{text}</Text>
                <Text size="xs" fw={600} mt={6} style={{ color }}>
                  {cta} <IconArrowRight size={12} style={{ verticalAlign: '-2px' }} aria-hidden="true" />
                </Text>
              </Box>
            </Box>
          );
        })}
      </SimpleGrid>

      {set.theme && related?.length > 0 && (
        <Box mt="xl">
          <Group justify="space-between" align="baseline" mb="sm">
            <Text fw={700} size="md" style={{ color: '#212529' }}>More {set.theme} sets</Text>
            <Anchor
              component={Link}
              href={`/?theme=${encodeURIComponent(set.theme)}`}
              size="sm"
              onClick={() => track('theme-all')}
            >
              View all <IconArrowRight size={14} style={{ verticalAlign: '-2px' }} aria-hidden="true" />
            </Anchor>
          </Group>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            {related.map(s => (
              <Link
                key={s.id}
                href={`/set/${s.id}`}
                className="whats-next-set"
                onClick={() => track('related-set')}
              >
                <Box className="whats-next-set-image">
                  {s.image_url && (
                    <img src={getThumbUrl(s.image_url, 192)} alt="" loading="lazy" decoding="async" />
                  )}
                </Box>
                <Text size="sm" fw={600} lineClamp={1} style={{ color: '#212529' }}>{s.name}</Text>
                <Text size="xs" c="dimmed">
                  #{s.id}{s.bag_count ? ` · ${s.bag_count} bags` : ''}
                </Text>
              </Link>
            ))}
          </SimpleGrid>
        </Box>
      )}
    </Paper>
  );
}

export default WhatsNext;
